import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { env } from '@/config/env';

/**
 * Sets per-page SEO metadata — BUILD-PLAN §39.
 *
 * Covers title, description, canonical and Open Graph tags. In a client-
 * rendered SPA this serves social/link previews and browser history; full
 * crawler coverage needs prerendering or SSR, which is Phase 36 work and is
 * recorded as outstanding in COMPLIANCE.md rather than implied here.
 *
 * Tags are removed on unmount so one page's description cannot linger on the
 * next.
 */

interface PageMetadata {
  title: string;
  description?: string | null;
  /** Absolute URL of a social sharing image. */
  image?: string | null;
  type?: 'website' | 'product' | 'article';
}

function upsertMeta(selector: string, attribute: string, key: string, content: string): HTMLElement {
  let element = document.head.querySelector<HTMLElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);

  return element;
}

export function usePageMetadata({ title, description, image, type = 'website' }: PageMetadata) {
  const { pathname } = useLocation();

  useEffect(() => {
    const previousTitle = document.title;
    const fullTitle = `${title} — ${env.appName}`;
    const canonicalUrl = `${window.location.origin}${pathname}`;

    document.title = fullTitle;

    const created: HTMLElement[] = [];

    created.push(
      upsertMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle),
      upsertMeta('meta[property="og:type"]', 'property', 'og:type', type),
      upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl),
      upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image'),
    );

    if (description) {
      created.push(
        upsertMeta('meta[name="description"]', 'name', 'description', description),
        upsertMeta('meta[property="og:description"]', 'property', 'og:description', description),
      );
    }

    if (image) {
      created.push(upsertMeta('meta[property="og:image"]', 'property', 'og:image', image));
    }

    // Canonical URL — §39. Prevents filter permutations being indexed as
    // separate pages, since the pathname excludes the query string.
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    return () => {
      document.title = previousTitle;
    };
  }, [title, description, image, type, pathname]);
}
